import { useEffect, useRef, useState, type ReactNode } from "react"
import Button from "@ui/button/Button"
import { formatCurrencyWithNumberToSymbolWithNumber as money } from "@ui/utils/formatter"
import Config from "src/Config"
import { systemErrorMessage, systemPost } from "src/services/superAdminApi"
import { superAdminFetch } from "src/services/superAdminFetch"
import { CardBalanceReturn } from "./CardBalanceReturn"
import { CompanyInvites } from "./CompanyInvites"
import { CompanyStatement } from "./CompanyStatement"
import { CryptoQuote } from "./CryptoQuote"
import { CompanyFees } from "./SuperAdminFees"
import { SuperAdminEvents } from "./SuperAdminEvents"
import { SuperAdminTransactions } from "./SuperAdminTransactions"
import { cardDisplayStatus, isCardBlocking } from "src/utils/cardStatus"
import { holderRole, kycVerdict, type UserFlowLike } from "src/utils/holderVerdict"
import { KycSummary, type IKycSummary } from "./KycSummary"
import styled from "styled-components"
import { T } from "../adminTheme"
import { ActionMenu, Avatar, date, Facts, KycState, linkRow, Muted, OpenCell, Page, Paged, Panel, Row, sentence, State, StatusBadge as Status, Table, Tabs } from "./adminUi"

export interface CompanyInfo {
	uid: string
	name: string
	type?: string
	status: string
	number?: string | null
	vat_number?: string | null
	created_at: string
	issuers?: Array<{ issuer: number; issuer_label: string; provider_code: string; is_default: boolean }>
	admins?: Array<{ uid: string; name: string; email: string }>
}
export interface CompanyAccount { issuer: number; name?: string; currency: string; balance: number | string | null }
export interface CompanyCard {
	uid: string; user_uid: string; company_uid: string; last4?: string; mask?: string; name?: string; status: string
	issuer?: number; provider_code?: string; balance: string; currency: string; created_at: string
	/** When the issuer confirmed the block; null otherwise. */
	blocked_at?: string | null
	/** True while cards/block runs; status stays active/frozen until the issuer confirms. */
	blocking?: boolean
	block_operation_uid?: string | null
}
interface CompanyUser {
	uid: string; name: string; email: string; status: string; kyc_status?: string; kyc_state?: string; company_role?: string
	cards_count?: number; active_cards_count?: number; cardholders_count?: number; approved_cardholders_count?: number
}
interface Deposit {
	id: number; company_uid?: string; user_uid?: string; coin_key: string; chain_key?: string; tx_hash?: string
	status: string; sweep_status: string; received_amount?: number; fee_cents?: number; credited_amount?: number
	credit_error?: string | null; sweep_error?: string | null; created_at: string
}
interface Commission { provider_code: string; type: string; our_percent: number; source?: string }
interface Overview { kyc?: unknown; cardholders?: Array<Record<string, unknown>>; flow?: UserFlowLike }

export interface CompanyActions {
	fund: () => void
	adjust: (direction: "in" | "out") => void
	admins: () => void
	toggleStatus: () => void
	adjustCard: (direction: "in" | "out", card: CompanyCard) => void
	reconcileCard: (card: CompanyCard) => void
}

interface Props {
	token: string
	company: CompanyInfo | null
	section?: string
	cardUid?: string
	onSection: (section: string) => void
	onOpenCard: (uid: string) => void
	onOpenUser: (uid: string) => void
	onBack: () => void
	loadAccounts: () => Promise<CompanyAccount[]>
	actions: CompanyActions
}

const SECTIONS = [
	["users", "Users & KYC"],
	["cards", "Cards"],
	["crypto", "Crypto deposits"],
	["transactions", "Transactions"],
	["events", "Events"],
	["statement", "Statement"],
	["fees", "Fees"],
	["invites", "Invites"],
	["rates", "Our rates (old)"]
] as const
/** Sections that render their own panels below the tab strip instead of inside it. */
const OWN_PANEL = ["transactions", "events", "invites"]

/** The API returns a masked number (mask); last4 only exists on older rows. */
const cardLabel = (card: CompanyCard) => card.mask || (card.last4 ? `•• ${card.last4}` : card.name || card.uid)
/** The shared formatter takes minor units (cents). */
const usd = (cents?: number) => cents === undefined ? "—" : money(cents, "USD")

const useLoad = <T,>(load: () => Promise<T>, deps: unknown[]) => {
	const [state, setState] = useState<{ data?: T; error?: string; loading: boolean }>({ loading: true })
	const [version, setVersion] = useState(0)
	const reloading = useRef(false)
	useEffect(() => {
		let active = true
		// A reload keeps the current data on screen instead of flashing a skeleton; a change of deps starts clean.
		const keep = reloading.current
		reloading.current = false
		setState((previous) => keep && previous.data !== undefined ? previous : { loading: true })
		load().then((data) => active && setState({ data, loading: false }), (cause) => active && setState({ error: cause instanceof Error ? cause.message : String(cause), loading: false }))
		return () => { active = false }
	}, [...deps, version]) // eslint-disable-line react-hooks/exhaustive-deps
	return { ...state, reload: () => { reloading.current = true; setVersion((value) => value + 1) } }
}

const post = async <T,>(token: string, method: string, body: Record<string, unknown>) => {
	const response = await systemPost<T>(token, method, body)
	if (response.error) throw new Error(systemErrorMessage(response.error))
	return response.data
}
const get = async <T,>(token: string, method: string, query: Record<string, string> = {}) => {
	const url = new URL(Config.scheme.URL_API + method)
	Object.entries(query).forEach(([key, value]) => url.searchParams.set(key, value))
	const response = await superAdminFetch(url.toString(), { headers: { Authorization: `Bearer ${token}` } })
	const payload = await response.json() as { data?: T; error?: { message?: string } }
	if (payload.error || !response.ok) throw new Error(payload.error?.message || response.statusText || "Request failed")
	return payload.data as T
}
const rows = <T,>(data: T[] | { rows?: T[] } | undefined) => Array.isArray(data) ? data : data?.rows ?? []

const Loadable = <V,>({ state, empty, emptyText, children }: { state: { data?: V; error?: string; loading: boolean }; empty?: boolean; emptyText?: string; children: (data: V) => ReactNode }) =>
	state.loading || state.error || empty ? <State loading={state.loading} error={state.error} empty={empty} emptyText={emptyText} /> : <>{children(state.data as V)}</>

const LINK_BUTTON = { border: 0, padding: "4px 8px", margin: "-4px 0", borderRadius: 6, background: "none", font: "inherit", fontSize: 13, color: T.accentText, fontWeight: 550, cursor: "pointer" } as const

const Verdict = styled.div<{ $ok: boolean; $neutral?: boolean }>`
	display: flex; gap: 10px; align-items: center; padding: 11px 14px; border-radius: 10px; margin-bottom: 16px; font-weight: 550;
	color: ${({ $ok, $neutral }) => $neutral ? T.text2 : $ok ? T.success : T.danger}; background: ${({ $ok, $neutral }) => $neutral ? T.surface2 : $ok ? T.successSoft : T.dangerSoft};
	box-shadow: inset 0 0 0 1px ${({ $ok, $neutral }) => $neutral ? T.border : $ok ? T.successLine : T.dangerLine};
	svg { flex: none; }
`

/** One sentence answer first; the working card holder; dead attempts only on request. */
const HolderTable = ({ verdict, holders, issuerLabel }: { verdict: { ok: boolean; neutral?: boolean; text: string }; holders: Array<Record<string, unknown>>; issuerLabel: (issuer: number) => string | undefined }) => {
	const [showOld, setShowOld] = useState(false)
	const old = holders.filter((holder) => holderRole(holder) === "old")
	const order = { active: 0, in_progress: 1, old: 2 } as const
	const shown = holders.filter((holder) => showOld || holderRole(holder) !== "old").sort((a, b) => order[holderRole(a)] - order[holderRole(b)])
	return <>
		<Verdict role='status' $ok={verdict.ok} $neutral={verdict.neutral}>
				<svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.2' strokeLinecap='round' strokeLinejoin='round'>{verdict.neutral ? <><circle cx='12' cy='12' r='9' /><path d='M12 11v5M12 8h.01' /></> : verdict.ok ? <><circle cx='12' cy='12' r='9' /><path d='m8 12 3 3 5-6' /></> : <><path d='M12 3 2 20h20L12 3z' /><path d='M12 10v4M12 17h.01' /></>}</svg>
				{verdict.text}
			</Verdict>
		{!verdict.neutral && (shown.length > 0 || old.length > 0) && <Table>
			<thead><tr><th>Card holder at issuer</th><th>Type</th><th title='Did the issuer accept this profile'>Accepted</th><th title="The issuer's own identity check">Issuer KYC</th><th>ID at issuer</th><th>Cards</th></tr></thead>
			<tbody>{shown.map((holder, index) => {
				const role = holderRole(holder)
				return <tr key={String(holder.uid ?? index)} style={role === "old" ? { color: T.muted } : undefined}>
					<td><span style={{ fontWeight: role === "old" ? 400 : 600 }}>{issuerLabel(Number(holder.issuer)) ?? String(holder.provider_name ?? holder.provider_code ?? "—")}</span><small>{role === "active" ? "In use" : role === "old" ? "Old attempt, not used" : "Being registered"}</small></td>
					<td>{String(holder.type ?? "—")}</td>
					{/* Old attempts do not matter any more, so their statuses stay grey instead of alarming. */}
					<td>{role === "old" ? <span style={{ textTransform: "capitalize" }}>{String(holder.status ?? "—").replaceAll("_", " ")}</span> : <Status value={holder.status as string} />}</td>
					<td>{role === "old" ? <span style={{ textTransform: "capitalize" }}>{String(holder.kyc_status ?? "—").replaceAll("_", " ")}</span> : <Status value={holder.kyc_status as string} />}</td>
					<td>{holder.provider_holder_id ? <code>{String(holder.provider_holder_id)}</code> : "—"}</td>
					<td>{String(holder.cards_count ?? "—")}</td>
				</tr>
			})}
			{old.length > 0 && <tr {...linkRow(() => setShowOld(!showOld))} aria-expanded={showOld}>
				<td colSpan={6} style={{ color: T.accentText, fontWeight: 550 }}>{showOld ? "Hide old attempts" : `Show ${old.length} old attempt${old.length === 1 ? "" : "s"}`}<span style={{ color: T.muted, fontWeight: 400 }}> · {showOld ? "they are not used" : "earlier tries that never reached the issuer, not used"}</span></td>
			</tr>}</tbody>
		</Table>}
	</>
}

export const SuperAdminCompany = ({ token, company, section = "users", cardUid, onSection, onOpenCard, onOpenUser, onBack, loadAccounts, actions }: Props) => {
	const uid = company?.uid ?? ""
	const users = useLoad(async () => !uid ? [] : rows(await post<CompanyUser[] | { rows: CompanyUser[] }>(token, "system/user/list", { company_uid: uid, with_meta: true, limit: 200 })), [token, uid])
	const cards = useLoad(async () => !uid ? [] : rows(await post<CompanyCard[] | { rows: CompanyCard[] }>(token, "system/cards/list", { company_uid: uid, with_meta: true, limit: 500 })), [token, uid])
	const accounts = useLoad(async () => uid ? loadAccounts() : [], [uid])
	const [kycUser, setKycUser] = useState("")
	const userList = users.data ?? []
	const focusUser = kycUser || company?.admins?.[0]?.uid || userList[0]?.uid || ""
	const overview = useLoad(async () => focusUser ? post<Overview>(token, "system/user/overview", { user_uid: focusUser, refresh: false }) : undefined, [token, focusUser])

	if (!company) return <Page><Row><Button variant='secondarySmall' onClick={onBack}>← Back</Button></Row><Panel><Muted>Company not found or still loading.</Muted></Panel></Page>

	const userName = (id: string) => { const user = userList.find((item) => item.uid === id); return user ? user.name || user.email : id }
	const issuerName = (issuer?: number) => company.issuers?.find((item) => item.issuer === issuer)?.issuer_label ?? (issuer ? `Issuer ${issuer}` : "—")
	const card = cardUid ? cards.data?.find((item) => item.uid === cardUid) : undefined
	const disabled = company.status === "disabled"

	return <Page>
		<Row style={{ gap: 12 }}>
			<Button variant='secondarySmall' onClick={onBack}>← Back</Button>
			<nav aria-label='Breadcrumb' style={{ fontSize: 13.5, color: T.muted }}>
				<span>Clients</span><Sep />{cardUid ? <><button type='button' onClick={() => onSection("cards")} style={{ ...LINK_BUTTON, margin: 0, padding: 0, color: T.muted, fontWeight: 400 }}>{company.name}</button><Sep /><span style={{ color: T.text, fontWeight: 550 }}>Card {card ? cardLabel(card) : cardUid}</span></> : <span style={{ color: T.text, fontWeight: 550 }}>{company.name}</span>}
			</nav>
		</Row>

		{/* Header panel must not clip the More actions dropdown. */}
		<Panel style={{ overflow: "visible", padding: 0 }}>
			<div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "center", justifyContent: "space-between", padding: "20px 22px" }}>
				<Row style={{ gap: 14, flexWrap: "nowrap", minWidth: 0 }}>
					<Avatar name={company.name} size={44} />
					<div style={{ minWidth: 0 }}>
						<Row style={{ gap: 10 }}><h2>{company.name}</h2><Status value={company.status} /></Row>
						<Muted style={{ fontSize: 13.5 }}>{company.type === "private" ? "Private account" : "Company"} · <code>{company.uid}</code></Muted>
					</div>
				</Row>
				<Row>
					<Button variant='primarySmall' onClick={actions.fund}>Fund balance</Button>
					<ActionMenu items={[
						["Adjust balance +", () => actions.adjust("in")],
						["Adjust balance −", () => actions.adjust("out")],
						["Manage admins", actions.admins],
						[disabled ? "Enable company" : "Disable company", actions.toggleStatus, !disabled]
					]} />
				</Row>
			</div>
			<Facts style={{ padding: "16px 22px 18px", borderTop: `1px solid ${T.border}`, background: T.surface2, borderRadius: "0 0 14px 14px" }}>
				<div><dt>Balance</dt><dd style={{ fontSize: 15 }}><Loadable state={accounts} empty={!accounts.data?.length} emptyText='No balance'>{(list) => list.map((account) => <div key={account.issuer}>{account.balance === null ? "—" : money(Number(account.balance), account.currency)}<Muted style={{ fontWeight: 400, fontSize: 13 }}> · {account.name || issuerName(account.issuer)}</Muted></div>)}</Loadable></dd></div>
				<div><dt>Connected issuers</dt><dd>{company.issuers?.length ? company.issuers.map((item) => item.issuer_label + (item.is_default && company.issuers!.length > 1 ? " (default)" : "")).join(", ") : "None"}</dd></div>
				<div><dt>Registration / VAT</dt><dd>{company.number || "—"} / {company.vat_number || "—"}</dd></div>
				<div><dt>Created</dt><dd>{date(company.created_at)}</dd></div>
			</Facts>
		</Panel>

		{cardUid
			? <CardView token={token} companyUid={company.uid} cardUid={cardUid} card={card} loading={cards.loading} onReturned={cards.reload} owner={card ? userName(card.user_uid) : ""} issuer={issuerName(card?.issuer)} onOpenUser={onOpenUser} actions={actions} />
			: <>
			<Panel style={{ paddingTop: 6, paddingBottom: OWN_PANEL.includes(section) ? 0 : 20 }}>
				<Tabs role='tablist' aria-label='Client sections' style={OWN_PANEL.includes(section) ? { marginBottom: 0, boxShadow: "none" } : undefined}>{SECTIONS.map(([key, label]) => <button key={key} role='tab' aria-selected={section === key} onClick={() => onSection(key)}>{label}{key === "users" && users.data ? <Count>{users.data.length}</Count> : key === "cards" && cards.data ? <Count>{cards.data.length}</Count> : null}</button>)}</Tabs>
				<div key={section} style={{ animation: `bo-fade .22s ${T.ease} both` }}>
					{section === "users" && <div style={{ display: "grid", gap: 22 }}>
						<Loadable state={users} empty={!userList.length} emptyText='No users in this company.'>{() => <Paged rows={userList}>{(pageRows) => <Table>
							<thead><tr><th>User</th><th>Role</th><th>Status</th><th>KYC</th><th>Active cards</th><th /></tr></thead>
							<tbody>{pageRows.map((user) => <tr key={user.uid} {...linkRow(() => setKycUser(user.uid))} data-active={user.uid === focusUser || undefined}>
								<td><Row style={{ gap: 10, flexWrap: "nowrap" }}><Avatar name={user.name || user.email} size={28} /><div><span style={{ fontWeight: 600 }}>{user.name || "—"}</span><small>{user.email}</small></div></Row></td>
								<td style={{ textTransform: "capitalize" }}>{user.company_role || "—"}</td>
								<td><Status value={user.status} /></td>
								<td><KycState state={user.kyc_state} status={user.kyc_status} /></td>
								<td>{user.active_cards_count ?? 0}<Muted> of {user.cards_count ?? 0}</Muted></td>
								<td style={{ textAlign: "right" }}><button type='button' onClick={(event) => { event.stopPropagation(); onOpenUser(user.uid) }} style={LINK_BUTTON}>Profile</button></td>
							</tr>)}</tbody>
						</Table>}</Paged>}</Loadable>
						{focusUser && <section key={focusUser} style={{ animation: `bo-rise .3s ${T.ease} both` }}>
							<div style={{ marginBottom: 12 }}><h3>KYC and card holders <Muted style={{ fontWeight: 400 }}>· {userName(focusUser)}</Muted></h3><Muted style={{ fontSize: 13 }}>Click a user above to switch. Can this user get and use cards, and which issuer profile the cards run on.</Muted></div>
							<Loadable state={overview}>{(data) => {
								const kyc = (data?.kyc && typeof data.kyc === "object" ? data.kyc : {}) as Record<string, unknown>
								const holders = data?.cardholders ?? []
								const verdict = kycVerdict(String(kyc.our_status ?? kyc.status ?? "") || undefined, holders, data?.flow)
								return <>
									{kyc.summary ? <KycSummary token={token} summary={kyc.summary as IKycSummary} /> : null}
									<HolderTable verdict={verdict} holders={holders} issuerLabel={(issuer) => company.issuers?.find((item) => item.issuer === issuer)?.issuer_label} />
								</>
							}}</Loadable>
						</section>}
					</div>}
					{section === "cards" && <Loadable state={cards} empty={!cards.data?.length} emptyText='This client has no cards yet.'>{(list) => <Paged rows={list}>{(pageRows) => <Table>
						<thead><tr><th>Card</th><th>Owner</th><th>Status</th><th style={{ textAlign: "right" }}>Balance</th><th>Issuer</th><th>Created</th><th /></tr></thead>
						<tbody>{pageRows.map((item) => <tr key={item.uid} {...linkRow(() => onOpenCard(item.uid))}>
							<td><Row style={{ gap: 12, flexWrap: "nowrap" }}><CardChip /><div><span style={{ fontWeight: 600 }}>{cardLabel(item)}</span><small>{item.name && item.name !== cardLabel(item) ? `${item.name} · ` : ""}{item.uid}</small></div></Row></td>
							<td>{userName(item.user_uid)}</td>
							<td><Status value={cardDisplayStatus(item)} />{item.blocked_at && <small>{date(item.blocked_at)}</small>}</td>
							<td style={{ textAlign: "right", fontWeight: 550 }}>{money(Number(item.balance || 0), item.currency)}</td>
							<td>{issuerName(item.issuer)}</td>
							<td style={{ color: T.text2 }}>{date(item.created_at)}</td>
							<OpenCell />
						</tr>)}</tbody>
					</Table>}</Paged>}</Loadable>}
					{section === "crypto" && <CryptoDeposits token={token} companyUid={company.uid} corporate={company.type === "company"} userName={userName} />}
					{section === "statement" && <CompanyStatement key={company.uid} token={token} companyUid={company.uid} />}
					{section === "fees" && <CompanyFees token={token} companyUid={company.uid} />}
					{section === "rates" && <Rates token={token} companyUid={company.uid} issuers={company.issuers ?? []} />}
				</div>
			</Panel>
			{section === "transactions" && <SuperAdminTransactions key={company.uid} token={token} companyUid={company.uid} />}
			{section === "events" && <SuperAdminEvents key={company.uid} token={token} initialFilters={{ company_uid: company.uid }} onOpenUser={onOpenUser} />}
			{section === "invites" && <CompanyInvites token={token} companyUid={company.uid} />}
			</>}
	</Page>
}

const Sep = () => <span aria-hidden style={{ margin: "0 8px", color: T.faint }}>/</span>
const Count = ({ children }: { children: ReactNode }) => <span style={{ marginLeft: 6, padding: "0 6px", borderRadius: 999, background: T.hover, color: T.muted, fontSize: 12, fontWeight: 550 }}>{children}</span>
/** A tiny card silhouette so card rows read as cards at a glance. */
const CardChip = ({ size = 1 }: { size?: number }) => <span aria-hidden style={{ width: 34 * size, height: 22 * size, flex: "none", borderRadius: 4 * size, background: "linear-gradient(135deg, #2A2A33 0%, #4B4B57 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,.18)", position: "relative", display: "inline-block" }}>
	<span style={{ position: "absolute", left: 5 * size, top: 7 * size, width: 7 * size, height: 5 * size, borderRadius: 1.5 * size, background: "linear-gradient(135deg, #E9C46A, #C9A23F)" }} />
</span>

const CardView = ({ token, companyUid, cardUid, card, loading, owner, issuer, onOpenUser, onReturned, actions }: {
	token: string; companyUid: string; cardUid: string; card?: CompanyCard; loading: boolean; owner: string; issuer: string
	onOpenUser: (uid: string) => void; onReturned: () => void; actions: CompanyActions
}) => {
	const [tab, setTab] = useState<"transactions" | "events">("transactions")
	return <>
		<Panel style={{ overflow: "visible" }}>
			{loading ? <State loading /> : !card ? <State empty emptyText={`Card ${cardUid} is not in this company.`} /> : <div style={{ display: "flex", flexWrap: "wrap", gap: 28, alignItems: "flex-start" }}>
				<PlasticCard>
					<span style={{ fontSize: 12, opacity: .7 }}>{issuer}</span>
					<span style={{ fontSize: 18, letterSpacing: ".08em", fontWeight: 550 }}>{cardLabel(card)}</span>
					<span style={{ display: "flex", justifyContent: "space-between", fontSize: 12, opacity: .8 }}><span>{owner}</span><span style={{ fontWeight: 600 }}>{money(Number(card.balance || 0), card.currency)}</span></span>
				</PlasticCard>
				<div style={{ flex: "1 1 360px", minWidth: 0, display: "grid", gap: 16 }}>
					<Row style={{ justifyContent: "space-between" }}>
						<Row style={{ gap: 10 }}><h2>Card {cardLabel(card)}</h2><Status value={cardDisplayStatus(card)} /></Row>
						<Row>
							<Button variant='secondarySmall' onClick={() => actions.reconcileCard(card)}>Check balance with issuer</Button>
							<ActionMenu items={[["Adjust balance +", () => actions.adjustCard("in", card)], ["Adjust balance −", () => actions.adjustCard("out", card)]]} />
						</Row>
					</Row>
					<Facts>
						<div><dt>Balance</dt><dd style={{ fontSize: 15 }}>{money(Number(card.balance || 0), card.currency)}</dd></div>
						<div><dt>Owner</dt><dd><button type='button' onClick={() => onOpenUser(card.user_uid)} style={{ ...LINK_BUTTON, margin: 0, padding: 0, fontSize: 14 }}>{owner}</button></dd></div>
						<div><dt>Issuer</dt><dd>{issuer}</dd></div>
						<div><dt>Created</dt><dd>{date(card.created_at)}</dd></div>
						{card.blocked_at && <div><dt>Blocked</dt><dd>{date(card.blocked_at)}</dd></div>}
						<div><dt>Card UID</dt><dd><code>{card.uid}</code></dd></div>
					</Facts>
				</div>
			</div>}
		</Panel>
		{card && <CardBalanceReturn key={card.uid} token={token} cardUid={card.uid} blocking={isCardBlocking(card)} onReturned={onReturned} />}
		<Panel style={{ paddingTop: 6, paddingBottom: 0 }}>
			<Tabs role='tablist' style={{ marginBottom: 0, boxShadow: "none" }}>
				<button role='tab' aria-selected={tab === "transactions"} onClick={() => setTab("transactions")}>Card transactions</button>
				<button role='tab' aria-selected={tab === "events"} onClick={() => setTab("events")}>Card events</button>
			</Tabs>
		</Panel>
		{tab === "transactions"
			? <SuperAdminTransactions key={cardUid} token={token} companyUid={companyUid} cardUid={cardUid} />
			: <SuperAdminEvents key={cardUid} token={token} initialFilters={{ card_uid: cardUid }} onOpenUser={onOpenUser} />}
	</>
}

const PlasticCard = styled.div`
	width: 280px; aspect-ratio: 1.586; flex: none; box-sizing: border-box; padding: 18px 20px; border-radius: 14px; color: #fff;
	display: flex; flex-direction: column; justify-content: space-between;
	background: radial-gradient(120% 140% at 0% 0%, #5B54E8 0%, #2B2577 55%, #16133F 100%);
	box-shadow: 0 1px 2px rgba(23,23,28,.2), 0 18px 36px -14px rgba(43,37,119,.55), inset 0 1px 0 rgba(255,255,255,.18);
	animation: bo-pop .4s ${T.ease} both;
`

const CryptoDeposits = ({ token, companyUid, corporate, userName }: { token: string; companyUid: string; corporate: boolean; userName: (uid: string) => string }) => {
	// ponytail: system/crypto/deposits has no company filter, so we load all and filter here; add company_uid on the API when the table grows.
	const deposits = useLoad(async () => (await get<Deposit[]>(token, "system/crypto/deposits")).filter((item) => item.company_uid === companyUid), [token, companyUid])
	return <div style={{ display: "grid", gap: 22 }}>
	<CryptoQuote token={token} companyUid={companyUid} corporate={corporate} coinKeys={deposits.data?.map((item) => item.coin_key)} />
	<Loadable state={deposits} empty={!deposits.data?.length}>{(list) => <Paged rows={list}>{(pageRows) => <Table>
		<thead><tr><th>Date</th><th>Coin</th><th>Received</th><th>Credited</th><th>Status</th><th>Sweep</th><th>User</th></tr></thead>
		<tbody>{pageRows.map((item) => <tr key={item.id}>
			<td>{date(item.created_at)}<small>#{item.id}</small></td>
			<td>{item.coin_key}{item.chain_key && <small>{item.chain_key}</small>}</td>
			<td>{usd(item.received_amount)}{item.fee_cents !== undefined && <small>fee {usd(item.fee_cents)}</small>}</td>
			<td>{usd(item.credited_amount)}</td>
			<td><Status value={item.status} />{item.credit_error && <small style={{ color: T.danger }}>{item.credit_error}</small>}</td>
			<td><Status value={item.sweep_status} />{item.sweep_error && <small style={{ color: T.danger }}>{item.sweep_error}</small>}</td>
			<td>{item.user_uid ? userName(item.user_uid) : "—"}{item.tx_hash && <small style={{ overflowWrap: "anywhere" }}>{item.tx_hash}</small>}</td>
		</tr>)}</tbody>
	</Table>}</Paged>}</Loadable>
	</div>
}

/** our_percent is stored in hundredths of a percent (250 = 2.50%). */
const PERCENT_SCALE = 100
const Rates = ({ token, companyUid, issuers }: { token: string; companyUid: string; issuers: NonNullable<CompanyInfo["issuers"]> }) => {
	const rates = useLoad(() => get<Commission[]>(token, "system/commissions/effective", { company_uid: companyUid }), [token, companyUid])
	const issuer = (code: string) => issuers.find((item) => item.provider_code === code)
	return <Loadable state={rates} empty={!rates.data?.length}>{(list) => <Table>
		<thead><tr><th>Issuer</th><th>Operation</th><th>Our percent</th><th>Source</th></tr></thead>
		{/* Connected issuers first: those are the rates the client actually pays. */}
		<tbody>{[...list].sort((a, b) => Number(Boolean(issuer(b.provider_code))) - Number(Boolean(issuer(a.provider_code)))).map((item) => {
			const connected = issuer(item.provider_code)
			return <tr key={`${item.provider_code}:${item.type}`} style={connected ? undefined : { color: T.muted }}>
				<td><span style={{ fontWeight: connected ? 600 : 400 }}>{connected?.issuer_label ?? item.provider_code}</span><small>{connected ? "Connected" : "Not connected to this client"}</small></td>
				<td>{sentence(item.type)}</td><td style={{ fontWeight: 550 }}>{(item.our_percent / PERCENT_SCALE).toFixed(2)}%</td>
				<td>{item.source === "company" ? "Set for this company" : "Default"}</td>
			</tr>
		})}</tbody>
	</Table>}</Loadable>
}
